"use client";

import React, { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/src/lib/utils";
import { Search, X } from "lucide-react";
import { format } from "date-fns";
import {
  SearchDestinationModal,
  GuestsModal,
  type IFilter,
} from "@/src/shared/features/search";
import DatePicker from "@/src/shared/components/DatePicker";

type TSearchSection = "where" | "checkIn" | "checkOut" | "guests";
const SECTION_ORDER: TSearchSection[] = [
  "where",
  "checkIn",
  "checkOut",
  "guests",
];

const LABEL_CLASS = "text-xs font-semibold text-ink";
const VALUE_CLASS = "truncate text-sm text-ink-secondary";
/** The active pill slides between sections, like Airbnb's (~200ms, fast out). */
/** The panel's grow-in and its morph between sections (~250ms, fast out). */
const PANEL_MORPH = { duration: 0.25, ease: [0.2, 0, 0, 1] } as const;
const PILL_TRANSITION = { duration: 0.2, ease: [0.2, 0, 0, 1] } as const;

const Divider = ({ hidden }: { hidden: boolean }) => (
  <span
    aria-hidden
    className={cn(
      "h-8 w-px shrink-0 bg-[#DDDDDD] transition-opacity duration-150",
      hidden && "opacity-0",
    )}
  />
);

const ClearButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    aria-label="Clear"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className="relative z-[3] ml-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#EBEBEB] hover:bg-[#DDDDDD]"
  >
    <X size={12} strokeWidth={3} className="text-ink" />
  </button>
);

interface IDesktopSearchProps {
  isSearchActive: boolean;
  headerState: "top" | "scrolled";
  activeSearchSection: "where" | "dates" | "guests" | null;
  filters: IFilter;
  setFilters: React.Dispatch<React.SetStateAction<IFilter>>;
  clearDestination: () => void;
  clearDates: () => void;
  clearGuests: () => void;
  getTotalGuests: () => string;
  activateSearch: (section: "where" | "dates" | "guests") => void;
  setActiveSearchSection: React.Dispatch<
    React.SetStateAction<"where" | "dates" | "guests" | null>
  >;
  updateGuestCount: (
    type: keyof IFilter["guests"],
    op: "add" | "subtract",
  ) => void;
  handleSearch: (next?: IFilter) => void;
}

const DesktopSearch: React.FC<IDesktopSearchProps> = ({
  isSearchActive,
  headerState,
  activeSearchSection,
  filters,
  setFilters,
  clearDestination,
  clearDates,
  clearGuests,
  getTotalGuests,
  activateSearch,
  setActiveSearchSection,
  updateGuestCount,
  handleSearch,
}) => {
  const [activeDateField, setActiveDateField] = React.useState<
    "checkIn" | "checkOut" | null
  >(null);

  useEffect(() => {
    if (activeSearchSection === "dates") {
      // Keep the field a click just picked (Check out); default to check-in otherwise.
      setActiveDateField((field) => field ?? "checkIn");
    } else {
      setActiveDateField(null);
    }
  }, [activeSearchSection]);

  const [hoveredSection, setHoveredSection] =
    React.useState<TSearchSection | null>(null);

  // One white pill marks the open panel; dates split into check-in / check-out.
  const activeSection: TSearchSection | null =
    activeSearchSection === "where"
      ? "where"
      : activeSearchSection === "guests"
        ? "guests"
        : activeSearchSection === "dates"
          ? activeDateField === "checkOut"
            ? "checkOut"
            : "checkIn"
          : null;

  // A divider fades out when either neighbour is hovered or holds the pill.
  const isDividerHidden = (index: number) =>
    [SECTION_ORDER[index], SECTION_ORDER[index + 1]].some(
      (section) => section === hoveredSection || section === activeSection,
    );

  const renderSection = (
    id: TSearchSection,
    flexClass: string,
    onActivate: () => void,
    content: React.ReactNode,
  ) => {
    const index = SECTION_ORDER.indexOf(id);
    const isActive = activeSection === id;
    const isHovered = hoveredSection === id && !isActive;
    // Next to the active pill, the hover grey runs on underneath it so no white
    // gap shows at the pill's rounded edge.
    const pillOnLeft = activeSection === SECTION_ORDER[index - 1];
    const pillOnRight = activeSection === SECTION_ORDER[index + 1];
    return (
      <div
        className={cn("relative h-full min-w-0", flexClass)}
        onMouseEnter={() => setHoveredSection(id)}
        onClick={onActivate}
      >
        {isHovered && (
          <span
            aria-hidden
            className={cn(
              "absolute inset-y-0 rounded-full",
              activeSection ? "bg-[#DDDDDD]" : "bg-[#EBEBEB]",
              pillOnLeft
                ? "-left-[70px] right-0"
                : pillOnRight
                  ? "-right-[70px] left-0"
                  : "inset-x-0",
            )}
          />
        )}
        {isActive && (
          <motion.span
            aria-hidden
            layoutId="search-active-pill"
            transition={PILL_TRANSITION}
            className="absolute inset-0 z-[1] rounded-full bg-white shadow-[0_3px_12px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.08)]"
          />
        )}
        <div className="relative z-[2] flex h-full cursor-pointer flex-col justify-center px-8">
          {content}
        </div>
      </div>
    );
  };

  // Scrolled down, the bar folds into the header's compact pill: its row collapses
  // (grid 1fr → 0fr) while the bar shrinks up and fades. It stays mounted so both
  // directions animate; `invisible` lands after the fade and drops it from tab order.
  const isExpanded = headerState === "top" || isSearchActive;

  return (
    <>
      <div
        aria-hidden={!isExpanded}
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <div
            // A fixed height: growing it when search opened dragged the panel down 20px.
            className={`h-20 origin-top transition-[opacity,transform,visibility] duration-300 ease-out ${
              isExpanded
                ? "opacity-100 translate-y-0 scale-100"
                : "invisible pointer-events-none opacity-0 -translate-y-8 scale-[0.6]"
            }`}
          >
            <div>
              <div className="mx-auto max-w-[850px]">
                <div
                  className={cn(
                    "relative flex h-[66px] items-center rounded-full border border-[#DDDDDD] transition-[background-color,box-shadow] duration-150 ease-linear",
                    activeSection
                      ? "bg-[#EBEBEB] shadow-none"
                      : "bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.02),0_8px_24px_rgba(0,0,0,0.1)]",
                  )}
                  onMouseLeave={() => setHoveredSection(null)}
                >
                  {renderSection(
                    "where",
                    "flex-[1.3]",
                    () => activateSearch("where"),
                    <>
                      <div className={LABEL_CLASS}>Where</div>
                      <div className="flex items-center">
                        <input
                          type="text"
                          placeholder="Search areas or hotels"
                          value={filters.destination?.name || ""}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              destination: {
                                name: e.target.value,
                                lat: null,
                                lng: null,
                                id: null,
                                name_bn: null,
                                order: null,
                                tier_1: null,
                                tier_2: null,
                                within: null,
                              },
                            })
                          }
                          className="w-full truncate bg-transparent text-sm text-ink-secondary outline-none placeholder:text-ink-secondary"
                        />
                        {filters.destination && activeSection === "where" && (
                          <ClearButton onClick={clearDestination} />
                        )}
                      </div>
                    </>,
                  )}
                  <Divider hidden={isDividerHidden(0)} />
                  {renderSection(
                    "checkIn",
                    "flex-[0.8]",
                    () => {
                      activateSearch("dates");
                      setActiveDateField("checkIn");
                    },
                    <>
                      <div className={LABEL_CLASS}>Check in</div>
                      <div className="flex items-center">
                        <span className={VALUE_CLASS}>
                          {filters.checkIn
                            ? format(filters.checkIn, "d MMM")
                            : "Add dates"}
                        </span>
                        {filters.checkIn && activeSection === "checkIn" && (
                          <ClearButton onClick={clearDates} />
                        )}
                      </div>
                    </>,
                  )}
                  <Divider hidden={isDividerHidden(1)} />
                  {renderSection(
                    "checkOut",
                    "flex-[0.8]",
                    () => {
                      activateSearch("dates");
                      setActiveDateField("checkOut");
                    },
                    <>
                      <div className={LABEL_CLASS}>Check out</div>
                      <div className="flex items-center">
                        <span className={VALUE_CLASS}>
                          {filters.checkOut
                            ? format(filters.checkOut, "d MMM")
                            : "Add dates"}
                        </span>
                        {filters.checkOut && activeSection === "checkOut" && (
                          <ClearButton onClick={clearDates} />
                        )}
                      </div>
                    </>,
                  )}
                  <Divider hidden={isDividerHidden(2)} />
                  {renderSection(
                    "guests",
                    "flex-[1.3]",
                    () => activateSearch("guests"),
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className={LABEL_CLASS}>Who</div>
                        <div className="flex items-center">
                          <span className={VALUE_CLASS}>
                            {getTotalGuests()}
                          </span>
                          {(filters.guests.adults > 1 ||
                            filters.guests.children > 0 ||
                            filters.guests.infants > 0) &&
                            activeSection === "guests" && (
                              <ClearButton onClick={clearGuests} />
                            )}
                        </div>
                      </div>
                      <motion.button
                        type="button"
                        onClick={(e) => {
                          // The section underneath would reopen the guests panel.
                          e.stopPropagation();
                          handleSearch();
                        }}
                        aria-label="Search"
                        initial={false}
                        animate={{ width: activeSection ? 100 : 48 }}
                        transition={PILL_TRANSITION}
                        className={cn(
                          "-mr-6 flex h-12 shrink-0 items-center justify-center gap-2 overflow-hidden rounded-full text-white",
                          activeSection
                            ? "bg-gradient-to-r from-brand-500 via-brand-500 to-brand-600"
                            : "bg-brand-500 hover:bg-gradient-to-r hover:from-brand-500 hover:via-brand-500 hover:to-brand-600",
                        )}
                      >
                        <Search
                          size={16}
                          strokeWidth={3}
                          className="shrink-0"
                        />
                        {activeSection && (
                          <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.15, delay: 0.05 }}
                            className="whitespace-nowrap text-base font-semibold"
                          >
                            Search
                          </motion.span>
                        )}
                      </motion.button>
                    </div>,
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* One white shell, like Airbnb's: it scales up from the top as it fades in, then
          morphs (size + position) between sections while the content inside crossfades
          — out in 75ms, in over 150ms after 50ms. */}
      <div className="pointer-events-none absolute left-1/2 top-full z-50 -mt-[2px] grid w-full max-w-[850px] -translate-x-1/2">
        <AnimatePresence>
          {isSearchActive && activeSearchSection && (
            <motion.div
              key="search-panel"
              layout
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{
                opacity: 0,
                scale: 0.95,
                transition: { duration: 0.1, ease: "linear" },
              }}
              transition={{
                opacity: { duration: 0.15, ease: "linear" },
                scale: PANEL_MORPH,
                layout: PANEL_MORPH,
              }}
              // Framer drives the origin itself; originY 0 grows the panel from its top edge.
              style={{ borderRadius: 32, originY: 0 }}
              className={cn(
                "pointer-events-auto col-start-1 row-start-1 overflow-hidden bg-white shadow-[0_2px_16px_rgba(0,0,0,0.12)]",
                activeSearchSection === "where"
                  ? "justify-self-start"
                  : activeSearchSection === "dates"
                    ? "justify-self-center"
                    : "justify-self-end",
              )}
            >
              <AnimatePresence initial={false} mode="popLayout">
                <motion.div
                  key={activeSearchSection}
                  layout
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: 1,
                    transition: { duration: 0.15, delay: 0.05, ease: "linear" },
                  }}
                  exit={{
                    opacity: 0,
                    transition: { duration: 0.075, ease: "linear" },
                  }}
                >
                  {activeSearchSection === "where" && (
                    <SearchDestinationModal
                      filters={filters}
                      setFilters={setFilters}
                      setActiveSearchSection={setActiveSearchSection}
                      onSearch={handleSearch}
                    />
                  )}
                  {activeSearchSection === "dates" && (
                    <DatePicker
                      selectedDates={{
                        checkIn: filters.checkIn,
                        checkOut: filters.checkOut,
                      }}
                      onDateChange={(dates) =>
                        setFilters((f) => ({ ...f, ...dates }))
                      }
                      onConfirm={() => setActiveSearchSection("guests")}
                      setActiveDateField={setActiveDateField}
                      // The shell draws the card; the picker's own shadow would double it.
                      containerClass="md:!shadow-none !rounded-none"
                    />
                  )}
                  {activeSearchSection === "guests" && (
                    <div className="w-[425px] px-8 py-4">
                      <GuestsModal
                        filters={filters}
                        updateGuestCount={updateGuestCount}
                        showRooms={(filters.destination?.name || "")
                          .toLowerCase()
                          .includes("cox")}
                      />
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default DesktopSearch;
